'use client'


import { usePathname } from 'next/navigation'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { useHomeBlocks, type NormalizedHomeBlock } from '@/lib/commerce/hooks'
import { navigateUrl } from '@/utils/client-request'
import { formatImageURL } from '@/utils/util'
import Hero from './Hero'
import Blogs from './Blogs'
import Products from './Products'
import AllProducts from './AllProducts'

function CategoryTiles({ pathname, categories }: { pathname: string, categories: any[] }) {
  const palette = [
    { bg: '#E8E2FF', fg: '#1a1130' },
    { bg: '#FFD9E7', fg: '#1a1130' },
    { bg: '#14082b', fg: '#ffffff' },
    { bg: '#FFE0C9', fg: '#1a1130' },
  ]
  const tiles = (categories || []).slice(0, 4).map((c, i) => ({
    label: c.name,
    sub: c.itemCount ? `${c.itemCount}+ бүтээгдэхүүн` : 'Шинэ цуглуулга',
    href: c.href || navigateUrl(`/product?category=${c.slug || c.id}`, pathname),
    image: c.thumbnail ? formatImageURL(c.thumbnail) : null,
    ...(c.thumbnail ? { bg: '#14082b', fg: '#ffffff' } : palette[i]),
  }))
  if (tiles.length === 0) return null
  return (
    <section className="bg-cream py-4 sm:py-8">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="flex items-end justify-between mb-8">
          <h2
            className="font-bold font-gilroy text-foreground leading-[0.96] tracking-[-0.025em]"
            style={{ fontSize: 'clamp(36px,4.5vw,60px)' }}
          >
            Хаанаас ч хайсан<br />зүйлээ ол.
          </h2>
          <a href={navigateUrl('/product', pathname)} className="hidden md:inline-flex items-center gap-1.5 text-[14px] text-foreground hover:underline group pb-2">
            Бүх ангилал <ArrowRight className="size-3.5 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {tiles.map(t => (
            <a key={t.label} href={t.href} className="group rounded-[20px] overflow-hidden relative aspect-3/4 flex flex-col p-6" style={{ background: t.bg, color: t.fg }}>
              {t.image && (
                <>
                  <img src={t.image} alt="" loading="lazy" decoding="async" className="absolute inset-0 size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none" />
                  <span aria-hidden className="absolute inset-0 bg-linear-to-t from-black/65 via-black/10 to-black/35" />
                </>
              )}
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] tracking-[0.18em] uppercase font-semibold opacity-70">{t.sub}</div>
                  <div className={`w-9 h-9 rounded-full backdrop-blur inline-flex items-center justify-center transition-colors ${t.image ? 'bg-white/20 text-white group-hover:bg-white group-hover:text-foreground' : 'bg-white/30 text-foreground group-hover:bg-foreground group-hover:text-white'}`}>
                    <ArrowUpRight className="size-4" />
                  </div>
                </div>
                <div className="mt-auto">
                  <div className="font-bold font-gilroy leading-[0.95] tracking-[-0.02em]" style={{ fontSize: 'clamp(28px,2.6vw,40px)' }}>
                    {t.label}
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}


const EYEBROW_BY_TYPE: Record<string, string> = {
  'sale-products': 'Express · 7 хоног',
  'new-products': 'Шинэ цуглуулга',
  'featured-products': 'Онцлох',
  'category': 'Ангилал',
}

export default function Home({ vendor, homeBlocks: homeBlocksProp, subscription, categories, session, activeCampaigns }: { vendor: any, homeBlocks?: any[], subscription?: any, categories?: any[], session?: any, wishlist?: any, activeCampaigns?: any[] }) {
  const blocks = useHomeBlocks({ vendor, homeBlocks: homeBlocksProp, subscription })
  const pathname = usePathname() || ''

  const tileSource = (categories || []).filter((c: any) => c.level === 0)
  const campaigns = activeCampaigns || []

  const renderBlock = (b: NormalizedHomeBlock) => {
    switch (b.type) {
    case 'banner':
      return <Hero key={b.id} vendor={vendor} activeCampaigns={campaigns} />
    case 'sale-products':
    case 'featured-products':
    case 'new-products':
    case 'category':
    case 'showcase':
      return (
        <Products
          key={b.id}
          eyebrow={EYEBROW_BY_TYPE[b.type]}
          title={b.title || ''}
          viewAllUrl={b.viewAllUrl}
          products={b.products || []}
          vendor={vendor}
          limit={b.config?.limit}
          description={b.description}
        />
      )
    case 'all-products':
      return <AllProducts key={b.id} vendor={vendor} title={b.title} description={b.description} session={session} />
    case 'blogs':
      return <Blogs key={b.id} blogs={b.blogs || []} title={b.title} limit={b.config?.limit} description={b.description} vendor={vendor} />
    default:
      return null
    }
  }

  return (
    <>
      <Hero vendor={vendor} activeCampaigns={campaigns} />
      <CategoryTiles pathname={pathname} categories={tileSource} />
      {blocks.filter(b => b.type !== 'banner').map(renderBlock)}
    </>
  )
}
